import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormControlLabel, Grid } from '@mui/material';
import { addMinutes, format, isToday, parseISO } from 'date-fns';
import { ETLJobRecurrenceEnum } from '~/api/types';
import { PickerInput } from '~/components';
import { PickerInputTypeEnum } from '~/components/PickerInput';
import { EndDateField, FrequencySelect } from '..';
import { scheduleBlockStyles } from '../styles';

const ScheduleExecutionBlock = () => {
  const {
    watch,
    control,
    setValue,
    formState: { errors, touchedFields },
  } = useFormContext();
  const formValues = watch();

  const today = format(new Date(), 'yyyy-MM-dd');
  const startDateString = formValues.start_date ? format(formValues.start_date, 'yyyy-MM-dd') : undefined;
  const isStartToday = startDateString ? isToday(parseISO(startDateString)) : false;
  const minTime = isStartToday ? format(addMinutes(new Date(), 5), 'HH:mm') : undefined;

  // Ensure that the selected time is not earlier than minTime if start date is today
  useEffect(() => {
    if (!isStartToday || !minTime || !formValues.start_date || !touchedFields.time) return;

    if (formValues.time < minTime) {
      setValue('time', minTime, { shouldValidate: true, shouldDirty: true });
    }
  }, [isStartToday, minTime, formValues.start_date, formValues.time, setValue, touchedFields.time]);

  return (
    <Grid container size={12} spacing={4} sx={scheduleBlockStyles}>
      <Grid size={6}>
        <Controller
          name="start_date"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              label="START DATE"
              required
              control={
                <PickerInput
                  error={!!errors.start_date}
                  helperText={errors.start_date?.message as string}
                  slotProps={{ htmlInput: { min: today } }}
                  type={PickerInputTypeEnum.DATE}
                  {...field}
                />
              }
            />
          )}
        />
      </Grid>
      <Grid size={6}>
        <Controller
          name="time"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              label="TIME"
              required
              control={
                <PickerInput
                  error={!!errors.time}
                  helperText={errors.time?.message as string}
                  slotProps={minTime ? { htmlInput: { min: minTime } } : undefined}
                  type={PickerInputTypeEnum.TIME}
                  {...field}
                />
              }
            />
          )}
        />
      </Grid>
      <Grid size={12}>
        <FrequencySelect />
      </Grid>
      {[ETLJobRecurrenceEnum.DAILY, ETLJobRecurrenceEnum.WEEKLY, ETLJobRecurrenceEnum.MONTHLY].includes(
        formValues.frequency,
      ) && (
        <Grid size={6}>
          <EndDateField />
        </Grid>
      )}
    </Grid>
  );
};

export default ScheduleExecutionBlock;
